/**
 * Media URL utilities for handling avatar and media file URLs
 * Ensures consistent URL handling across development and production environments
 */

/**
 * Get the base URL for media files based on environment
 * @returns {string} Base URL for media files
 */
export const getMediaBaseUrl = () => {
    if (process.env.NODE_ENV === 'production') {
        return '';
    }
    return 'http://localhost:8001';
};

/**
 * Convert relative media URLs to absolute URLs
 * @param {string} url - Relative URL like "/media/avatars/..." or full URL
 * @returns {string|null} - Absolute URL or null if no URL provided
 */
export const getAbsoluteMediaUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url; // Already absolute
    
    const baseURL = getMediaBaseUrl();
    return `${baseURL}${url}`;
};

/**
 * Add cache busting parameter to avatar URLs
 * @param {string} url - Avatar URL
 * @param {number} timestamp - Optional timestamp, defaults to current time
 * @returns {string} - URL with cache busting parameter
 */
export const addCacheBuster = (url, timestamp = null) => {
    if (!url) return null;
    
    const cacheBuster = timestamp || Date.now();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${cacheBuster}`;
};

/**
 * Process employee data to ensure avatar URLs are absolute
 * @param {Object|Array} data - Employee object or array of employees
 * @returns {Object|Array} - Processed data with absolute avatar URLs
 */
export const processEmployeeAvatars = (data) => {
    if (!data) return data;
    
    if (Array.isArray(data)) {
        return data.map(emp => ({
            ...emp,
            avatar_photo: getAbsoluteMediaUrl(emp.avatar_photo)
        }));
    }
    
    // Single employee object
    return {
        ...data,
        avatar_photo: getAbsoluteMediaUrl(data.avatar_photo)
    };
};

export default {
    getMediaBaseUrl,
    getAbsoluteMediaUrl,
    addCacheBuster,
    processEmployeeAvatars
};
